import { useContext, useEffect, useState } from "react"
import { AppCenterPopupD3, CenterPopupHeaderD3 } from "src/component"
import ApiContext from "src/provider/API/call-service"
import AppInfoContext from "src/provider/state-manager/appInfoProvider"
import UserContext from "src/provider/state-manager/userProvider"
import { ScreenTitle, CustomContainer, AbsoluteContainer, AppSpan, AppText, FlexColumn, FlexRow, Icon, Circle, GridContainer, FormGroupWithIcon } from "src/style"
import cardMesh from 'src/assets/img/card-mesh.svg'
import MastercardLogoDark from 'src/assets/img/mastercard-logo-dark.svg'
import cardWifi from 'src/assets/img/card-wifi.svg'
import { BiCheck } from "react-icons/bi"
import { AiTwotoneStop } from "react-icons/ai"
import {ReactComponent as NoDataIcon} from 'src/assets/img/no-data-icon-d3.svg'
import { moneyFormat } from "src/utils/utility"
import { FiChevronDown } from "react-icons/fi"
import { CardRequestStatusData } from "src/model"
import helpers from "src/utils/helpers"
import { IoCloseOutline } from "react-icons/io5"

export const CardRequestStatusD3: React.FC<any> = ({close}) => {
    const {API} = useContext(ApiContext)
    const {info: {institutionData}} = useContext(AppInfoContext)
    const {user: {accountWithBalanceDetails}} = useContext(UserContext)
    const [cardRequestData, setCardRequestData] = useState<CardRequestStatusData | null>(null)
    const [accountNumber, setAccountNumber] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const date = helpers.splitDateTime(cardRequestData?.createdAt ? cardRequestData.createdAt : '0001-01-01T00:00:00.0Z')
    const hasMadeApiCall = cardRequestData || (!cardRequestData && accountNumber)

    useEffect(() => {
        if (accountWithBalanceDetails?.length === 1) handleAccountNumberChange(accountWithBalanceDetails[0].nuban)
    }, [accountWithBalanceDetails])

    async function handleAccountNumberChange(acctNumber: string) {
        setAccountNumber(acctNumber)
        setIsLoading(true)
        const response = await API.getCardRequestStatus(acctNumber)
        setIsLoading(false)
        setCardRequestData(response)
    }

    return (
        <AppCenterPopupD3 height={hasMadeApiCall ? '65' : '27'} zIndex='6' overflow='hidden' close={close}>
            <CenterPopupHeaderD3 title='Card Request Status' close={close} />
            { (isLoading) &&
                <AbsoluteContainer top='0' left='0' zIndex='3' width='100' sizeUnit='%' height="full">
                    <GridContainer height='100' hUnit="%" bgColor='rgba(255, 255, 255, 0.5)' radius="2">
                        <AppSpan textSize='4' color='#0D968F' className='fa fa-spinner fa-spin' />
                    </GridContainer>
                </AbsoluteContainer>
            }
            <CustomContainer 
                height={hasMadeApiCall ? '65' : '27'}
                style={{maxHeight: '85%'}}
                className="popup-content-container"
            >
                <FormGroupWithIcon>
                    <label>Account</label>
                    <div className="input-with-icon">
                        <select 
                            name='accountNumber'
                            value={accountNumber}
                            style={{color: accountNumber ? '#000000' : '#667085'}}
                            onChange={(e) => handleAccountNumberChange(e.target.value)}
                            disabled={accountWithBalanceDetails?.length === 1}
                            required
                        >
                            <option value="">Select Account</option>
                            {
                                accountWithBalanceDetails?.map((item: any, index: number) => {
                                    return (
                                        <option 
                                            value={item.nuban} 
                                            key={index}
                                        >
                                            {item.nuban} - ({moneyFormat(item?.balance?.withdrawableAmount,institutionData?.defaultCurrency)})
                                        </option>
                                    )
                                })
                            }
                        </select>
                        <FiChevronDown className="input-with-icon-tag" />
                    </div>
                </FormGroupWithIcon>
                { cardRequestData &&
                    <>
                        <CustomContainer 
                            width='100' sizeUnit="%" radius='1.45' bgColor='#0D968F'
                            shadow='5.79747px 7.24684px 11.5949px rgba(0, 0, 0, 0.05)'
                        >
                            <AbsoluteContainer bottom='0' left='0' width='100' sizeUnit='%'>
                                <Icon src={cardMesh} noResize minWidth='100' style={{objectFit: 'cover'}} />
                            </AbsoluteContainer>
                            <AbsoluteContainer top='2' right='2'>
                                <CustomContainer width='2.1' height='2.5'>
                                    <Icon src={cardWifi} minWidth='100' /> 
                                </CustomContainer>
                            </AbsoluteContainer>
                            
                            <FlexColumn 
                                minHeight='20.6' justifyContent='space-between' padding='1'
                                alignItems='flex-start' topPadding='1.5' bottomPadding='1.5'
                                style={{zIndex: '2'}}
                            >
                                <AppText textSize='1.6' fontWeight='600' color='#ffffff'>{institutionData?.name}</AppText>
                                <CustomContainer topMargin='2'>
                                    <FlexRow width='auto' sizeUnit='' gap='5' bottomMargin='0.5'>
                                        <AppSpan textSize='1.2' color='#ffffff' fontWeight='600' fontFamily="Manrope">{cardRequestData?.nameOnCard?.toUpperCase()}</AppSpan>
                                        <AppSpan textSize='1.6' color='#ffffff' fontWeight='600' fontFamily="Manrope">XX/XX</AppSpan>
                                    </FlexRow>
                                    <AppText textSize='1.6' color='#ffffff' fontWeight='600' fontFamily="Manrope">XXXX XXXXX XXXX</AppText>
                                </CustomContainer>
                                <AbsoluteContainer bottom='1.5' right='1'>
                                    <CustomContainer width='4.6' height='3.3'>
                                        <Icon src={MastercardLogoDark} minWidth='100' /> 
                                    </CustomContainer>
                                </AbsoluteContainer>
                            </FlexColumn>
                        </CustomContainer>
                        <AppText color="#667085" topMargin="2" fontWeight="600">Requested on: {date?.date}</AppText>
                        <CustomContainer topMargin="1">
                            <FlexRow gap="1.5" alignItems="flex-start">
                                <GridContainer 
                                    width="auto" sizeUnit="" 
                                    style={{flexBasis: 'auto', flexShrink: '0', flexGrow: '0'}} 
                                >
                                    {  cardRequestData.status === 'Declined' &&
                                        <>
                                            <Circle 
                                                size="1.6" 
                                                style={{backgroundColor: '#FF0000'}}
                                            >
                                                <IoCloseOutline style={{color: '#ffffff', fontSize: '1.2rem'}} />
                                            </Circle>
                                            <CustomContainer 
                                                width="0" height="3.5" topMargin="0.3" bottomMargin="0.3"
                                                style={{border: '1px solid #D9D9D9'}}
                                            />
                                        </>
                                    }
                                    <Circle 
                                        size="1.6" 
                                        style={{
                                            backgroundColor: statusMap[cardRequestData.status] < 2 ? '#ffffff' : '#0D968F',
                                            border: `1px solid  ${statusMap[cardRequestData.status] < 1 ? '#D9D9D9' : statusMap[cardRequestData.status] < 2 ? '#F9A000' : '#0D968F'}`
                                        }}
                                    >
                                        { statusMap[cardRequestData.status] < 2 ?
                                            <AiTwotoneStop style={{color: statusMap[cardRequestData.status] < 1 ? '#D9D9D9' : '#F9A000', fontSize: '0.5rem'}} /> 
                                            :<BiCheck style={{color: '#ffffff', fontSize: '1.2rem'}} />
                                        }
                                    </Circle>
                                    <CustomContainer 
                                        width="0" height="3.5" topMargin="0.3" bottomMargin="0.3"
                                        style={{border: `1px ${statusMap[cardRequestData.status] < 2 || statusMap[cardRequestData.status] >= 3 ? 'solid' : 'dashed'} ${statusMap[cardRequestData.status] < 2 ? '#D9D9D9' : '#0EA59D'}`}}
                                    />
                                    <Circle 
                                        size="1.6" 
                                        style={{
                                            backgroundColor: statusMap[cardRequestData.status] < 3 ? '#ffffff' : '#0D968F',
                                            border: `1px solid  ${statusMap[cardRequestData.status] < 2 ? '#D9D9D9' : statusMap[cardRequestData.status] < 3 ? '#F9A000' : '#0D968F'}`
                                        }}
                                    >
                                        { statusMap[cardRequestData.status] < 3 ?
                                            <AiTwotoneStop style={{color: statusMap[cardRequestData.status] < 2 ? '#D9D9D9' : '#F9A000', fontSize: '0.5rem'}} /> 
                                            :<BiCheck style={{color: '#ffffff', fontSize: '1.2rem'}} />
                                        }
                                    </Circle>
                                    <CustomContainer 
                                        width="0" height="3.5" topMargin="0.3" bottomMargin="0.3"
                                        style={{border: `1px ${statusMap[cardRequestData.status] < 3 || statusMap[cardRequestData.status] >= 4 ? 'solid' : 'dashed'} ${statusMap[cardRequestData.status] < 3 ? '#D9D9D9' : '#0EA59D'}`}}
                                    />
                                    <Circle 
                                        size="1.6" borderColor="#F9A000"
                                        style={{
                                            backgroundColor: statusMap[cardRequestData.status] < 4 ? '#ffffff' : '#0D968F',
                                            border: `1px solid  ${statusMap[cardRequestData.status] < 3 ? '#D9D9D9' : statusMap[cardRequestData.status] < 4 ? '#F9A000' : '#0D968F'}`
                                        }}
                                    >
                                         { statusMap[cardRequestData.status] < 4 ?
                                            <AiTwotoneStop style={{color: statusMap[cardRequestData.status] < 3 ? '#D9D9D9' : '#F9A000', fontSize: '0.5rem'}} /> 
                                            :<BiCheck style={{color: '#ffffff', fontSize: '1.2rem'}} />
                                        }
                                    </Circle>
                                </GridContainer>
                                <CustomContainer>
                                    { cardRequestData.status === 'Declined' &&
                                        <CustomContainer>
                                            <AppSpan color="#1A1A1A" fontWeight="600">Declined</AppSpan>
                                            <AppText color="#667085" fontWeight="600" textSize="1">Card request was declined by the institution</AppText>
                                        </CustomContainer>
                                    }
                                    <CustomContainer topMargin={cardRequestData.status === 'Declined' ? "1.8" : "0"}>
                                        <AppSpan color={statusMap[cardRequestData.status] >= 2 ? "#1A1A1A" : "#D9D9D9"} fontWeight="600">Approved</AppSpan>
                                        <AppText color={statusMap[cardRequestData.status] >= 2 ? "#667085" : "#D9D9D9"} fontWeight="600" textSize="1">Your card generation has been approved</AppText>
                                    </CustomContainer>
                                    <CustomContainer topMargin="1.8">
                                        <AppSpan color={statusMap[cardRequestData.status] >= 3 ? "#1A1A1A" : "#D9D9D9"} fontWeight="600">In Production</AppSpan>
                                        <AppText color={statusMap[cardRequestData.status] >= 3 ? "#667085" : "#D9D9D9"} fontWeight="600" textSize="1">Your card PAN has been generated by the issuer</AppText>
                                    </CustomContainer>
                                    <CustomContainer topMargin="2">
                                        <AppSpan color={statusMap[cardRequestData.status] >= 4 ? "#1A1A1A" : "#D9D9D9"} fontWeight="600">Ready For Pickup</AppSpan>
                                        <AppText color={statusMap[cardRequestData.status] >= 4 ? "#667085" : "#D9D9D9"} fontWeight="600" textSize="1">Your card is ready for pickup</AppText>
                                    </CustomContainer>
                                </CustomContainer>
                            </FlexRow>
                        </CustomContainer>
                    </>
                }
                { (!cardRequestData && accountNumber && !isLoading) &&
                    <GridContainer topMargin="14">
                        <CustomContainer width="auto" sizeUnit="">
                            <GridContainer>
                                <NoDataIcon className="icon-component" />
                            </GridContainer>
                            <AppText textSize="1.6" fontWeight="600" color="#101828" align="center" topMargin="1.5">
                                No Pending Request
                            </AppText>
                        </CustomContainer>
                    </GridContainer>
                }
            </CustomContainer>
        </AppCenterPopupD3>
    )
}

const statusMap = {
    'Validated': 1,
    'Approved': 2,
    'Generated': 3,
    'FullyReceived': 4,
    'FullyRecieved': 4,
    'Declined': 0
}